import {
  useRef,
  useState,
  FormEvent,
  useEffect,
  useContext,
  ChangeEvent,
} from 'react';
import {
  Box,
  Flex,
  Text,
  Form,
  Modal,
  toast,
  Devider,
  FormContext,
  ModalContext,
  FormErrorTypes,
  IOnUploadOptionsProps,
  WorkingWeekHoursInput,
  useServerResponseTranslation,
} from 'dash-ui';
import { ErrorCode } from 'react-dropzone';
import { useTheme } from 'styled-components';
import { DashValidation, FileModel, WeekDays } from 'dash-common';
import { useTranslation } from 'react-i18next';

import {
  GetSaleTeamsDocument,
  useCreateSaleTeamMutation,
  useEditSaleTeamMutation,
} from 'gql/generated.graphql';
import useFormDraftFileUpload from 'common/hooks/useDraftFileUpload';
import { ExternalSalesModalKeys } from 'pages/external-sales/constants/constants';
import { SaveSaleFormFields } from 'pages/external-sales/external-sale-teams/constants';
import { useSaveExternalTeamFormValidation } from 'pages/external-sales/external-sale-teams/hooks';

const SaveExternalSaleModal = () => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'forms', 'pages']);
  const formRef = useRef<HTMLFormElement | null>(null);
  const {
    toggleModal,
    isOpenCurrent,
    payload: saleTeam,
    setPayload,
  } = useContext(ModalContext);
  const { validate, isError, setFormValues } =
    useSaveExternalTeamFormValidation();
  const [createSaleTeam, { loading: createSaleTeamLoading }] =
    useCreateSaleTeamMutation({
      refetchQueries: [GetSaleTeamsDocument],
    });
  const [editSaleTeam, { loading: editSaleTeamLoading }] =
    useEditSaleTeamMutation({
      refetchQueries: [GetSaleTeamsDocument],
    });
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    if (saleTeam?._id) {
      const { name, website, logoFile, phone, workingHours } = saleTeam;

      setFormValues({
        name,
        url: website,
        workingHours,
        logo: logoFile?._id,
        phone: { number: phone?.number, dialCode: phone?.dialCode },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleTeam]);

  const translateServerError = useServerResponseTranslation();
  const {
    stateHandlers,
    errorHandlers,
    formState,
    formErrors,
    formOnSubmit,
    resetForm,
    resetFormErrors,
  } = useContext(FormContext);

  const [isLogoLoading, setIsLogoLoading] = useState(false);
  const isEditMode = !!saleTeam?._id;

  const isOpen = isOpenCurrent[ExternalSalesModalKeys.SaveExternalSaleTeam];
  const modalTitle = isEditMode
    ? t('pages:externalSalesPage.editExternalTeam')
    : t('pages:externalSalesPage.addExternalTeam');
  const draftFileUpload = useFormDraftFileUpload();
  const closeModal = () => {
    toggleModal(false, ExternalSalesModalKeys.SaveExternalSaleTeam);
    resetForm();
    resetFormErrors();
    setPayload({});
  };

  const handleFieldsValues =
    (field: SaveSaleFormFields) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      stateHandlers[field](value);
      validate(field, value);
    };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();

    const { name, url, logo, phone, workingHours } = formState;
    const variables = {
      ...(saleTeam?._id && { saleTeamId: saleTeam._id }),
      input: {
        name,
        website: url,
        logoFileId: logo,
        ...(phone?.dialCode && phone?.number && { phone }),
        ...(workingHours && {
          workingHours: Object.values(WeekDays).reduce(
            (memo: any, key: string) => {
              const { to, from } = workingHours[key] || {};

              return { ...memo, ...(to && from && { [key]: { to, from } }) };
            },
            {}
          ),
        }),
      },
    };

    const saveFn = saleTeam?._id ? editSaleTeam : createSaleTeam;

    saveFn({
      variables,
      onCompleted: () => {
        const textNotify = isEditMode ? 'teamChanged' : 'teamAdded';
        toast.success(t(`pages:externalSalesPage.toasts.${textNotify}`));

        closeModal();
      },
      onError: (error: any) => {
        toast.error(translateServerError(error));
      },
    });
  };

  const onFileChanged = (fileId: string) => {
    stateHandlers[SaveSaleFormFields.Logo](fileId || null);
    errorHandlers[SaveSaleFormFields.Logo](null);
    setIsLogoLoading(false);
  };

  const onFileUpload = (file: File, options: IOnUploadOptionsProps) => {
    errorHandlers[SaveSaleFormFields.Logo](null);
    setIsLogoLoading(true);
    draftFileUpload(file, options);
  };

  const isLoading =
    isLogoLoading || createSaleTeamLoading || editSaleTeamLoading;

  return (
    <Modal
      isOpen={isOpen}
      maxWidth={979}
      maxHeight="100%"
      h={['100%', 0, 0, 0]}
      w="100%"
      onRequestClose={closeModal}
      title={modalTitle}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
      submitButton={{
        isLoading,
        disabled: isError || isLoading,
        title: isEditMode ? t('common:labels.edit') : t('common:labels.add'),
        onClick: () => formRef.current && formRef.current.requestSubmit(),
      }}
    >
      <Form onSubmit={submitForm} ref={formRef}>
        <Flex>
          <Box flex={1}>
            <Form.Group mt={1} pr={5}>
              <Form.Label>{t`forms:labels.teamLogo`}</Form.Label>
              <Form.FileUpload.Single
                minHeight="146px"
                accept={{ 'image/png': ['.png'], 'image/jpg': ['.jpg'] }}
                onUpload={onFileUpload}
                onChanged={onFileChanged}
                initialFile={saleTeam?.logoFile as FileModel}
                maxSize={DashValidation.MaxLogoFileSize}
                onDropRejected={(error) => {
                  let message = 'forms:messages:uploadErrors:unknown';
                  if (error.code === ErrorCode.FileInvalidType) {
                    message = 'forms:messages:uploadErrors:fileInvalidType';
                  }
                  if (error.code === ErrorCode.FileTooLarge) {
                    message = 'forms:messages:uploadErrors:fileTooLarge';
                  }
                  errorHandlers[SaveSaleFormFields.Logo]({
                    type: FormErrorTypes.Invalid,
                    message: t(message, {
                      type: '.png, .jpg',
                      maxSize: DashValidation.MaxLogoFileSize / 1024 / 1024,
                    }),
                  });
                }}
                $errorType={formErrors[SaveSaleFormFields.Logo]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[SaveSaleFormFields.Logo]?.type}
              >
                {formErrors[SaveSaleFormFields.Logo]?.message}
              </Form.Feedback>
            </Form.Group>
          </Box>
          <Box flex={1}>
            <Form.Group mt={1}>
              <Form.Label required>{t`labels.name`}</Form.Label>
              <Form.Input
                w="100%"
                placeholder={t`forms:placeholders.saleTeamName` || ''}
                onChange={handleFieldsValues(SaveSaleFormFields.Name)}
                value={formState.name || ''}
                $errorType={formErrors[SaveSaleFormFields.Name]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[SaveSaleFormFields.Name]?.type}
              >
                {formErrors[SaveSaleFormFields.Name]?.message}
              </Form.Feedback>
            </Form.Group>
            <Form.Group mt={1}>
              <Form.Label>{t`forms:labels.url`}</Form.Label>
              <Form.Input
                w="100%"
                placeholder={t`forms:placeholders.url` || ''}
                onChange={handleFieldsValues(SaveSaleFormFields.Url)}
                value={formState.url || ''}
                $errorType={formErrors[SaveSaleFormFields.Url]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[SaveSaleFormFields.Url]?.type}
              >
                {formErrors[SaveSaleFormFields.Url]?.message}
              </Form.Feedback>
            </Form.Group>
            <Form.Group w="100%" mt={1}>
              <Form.Label>{t('forms:labels.phoneNumber')}</Form.Label>
              <Form.InputPhone
                dialCode={formState.phone?.dialCode || ''}
                defaultValue={formState.phone?.number || ''}
                placeholder={t('forms:placeholders.phoneNumber') || ''}
                onChangePhoneValue={({ dialCode, number }) =>
                  stateHandlers[SaveSaleFormFields.Phone]({
                    dialCode,
                    number,
                  })
                }
              />
            </Form.Group>
          </Box>
        </Flex>
        <Devider size={1} my={4} color={theme.colors.n30} />
        <Box mb={4}>
          <Text bold>{t('forms:labels.salesWorkingHours')}</Text>
        </Box>
        <WorkingWeekHoursInput formField={SaveSaleFormFields.WorkingHours} />
        <Form.Group mt={4}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default SaveExternalSaleModal;
